import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import {
  addPost,
  addVideoPost,
  getUserSettings,
} from "../firebase";

import {
  AiOutlineCamera,
  AiOutlineVideoCamera,
  AiOutlineCloseCircle,
  AiOutlineEnvironment,
  AiOutlineLink,
} from "react-icons/ai";

import { FaUserCircle } from "react-icons/fa";

import "../styles/main.css";

const AREAS = [
  "All",
  "Tiruchengode",
  "Sankagiri",
  "Kumarapalayam",
  "Pallipalayam",
];

export default function PostComposer() {

  const { user } = useAuth();

  const [userPhoto, setUserPhoto] = useState("");

  const [text, setText] = useState("");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [fileType, setFileType] = useState("");

  const [selectedArea, setSelectedArea] = useState("");
  const [showLocationPicker, setShowLocationPicker] = useState(false);

  const [posting, setPosting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const [urlLink, setUrlLink] = useState("");
  const [showAttachUrlModal, setShowAttachUrlModal] = useState(false);
  const [tempUrlInput, setTempUrlInput] = useState("");

  // Load user photo
  useEffect(() => {

    const loadUser = async () => {

      if (!user?.uid) return;

      try {

        const settings = await getUserSettings(user.uid);

        setUserPhoto(
          settings?.photoURL ||
          user?.photoURL ||
          ""
        );

      } catch (error) {

        console.error("Post composer user error:", error);

        setUserPhoto(user?.photoURL || "");

      }

    };

    loadUser();

  }, [user]);


  // ImgBB upload
  const uploadToImgBB = async (file) => {

    const key = process.env.REACT_APP_IMGBB_KEY;

    if (!file) {
      throw new Error("No image selected.");
    }

    if (!key) {
      throw new Error(
        "ImgBB API key is missing. Check REACT_APP_IMGBB_KEY."
      );
    }

    const fd = new FormData();

    fd.append("image", file);

    const res = await fetch(
      `https://api.imgbb.com/1/upload?key=${key}`,
      {
        method: "POST",
        body: fd,
      }
    );

    const data = await res.json();

    if (!res.ok || !data.success || !data?.data?.url) {

      throw new Error(
        data?.error?.message ||
        "ImgBB image upload failed."
      );

    }

    return data.data.url;

  };


  // Cloudinary video upload
  const uploadToCloudinary = async (file) => {

    return new Promise((resolve, reject) => {

      const cloudName =
        process.env.REACT_APP_CLOUDINARY_CLOUD_NAME;

      const preset =
        process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET;

      if (!file || !cloudName || !preset) {

        reject(
          new Error(
            "Cloudinary configuration is missing."
          )
        );

        return;

      }

      const xhr = new XMLHttpRequest();

      const fd = new FormData();

      fd.append("file", file);
      fd.append("upload_preset", preset);
      fd.append("resource_type", "video");

      xhr.upload.addEventListener(
        "progress",
        (e) => {

          if (e.lengthComputable) {

            setUploadProgress(
              Math.round(
                (e.loaded * 100) / e.total
              )
            );

          }

        }
      );

      xhr.onreadystatechange = () => {

        if (xhr.readyState !== 4) return;

        if (xhr.status === 200) {

          try {

            resolve(
              JSON.parse(xhr.responseText)
            );

          } catch (error) {

            reject(error);

          }

        } else {

          reject(
            new Error(
              xhr.statusText ||
              "Video upload failed."
            )
          );

        }

      };

      xhr.open(
        "POST",
        `https://api.cloudinary.com/v1_1/${cloudName}/video/upload`
      );

      xhr.send(fd);

    });

  };


  // URL helpers
  const normalizeUrl = (value) => {

    if (!value) return "";

    return /^https?:\/\//i.test(value)
      ? value
      : "https://" + value;

  };


  const openAttachUrlModal = () => {

    setTempUrlInput(urlLink);

    setShowAttachUrlModal(true);

  };


  const attachUrl = () => {

    const value =
      normalizeUrl(
        tempUrlInput.trim()
      );

    if (
      !value ||
      /^javascript:/i.test(value)
    ) {

      alert("Enter a valid URL");

      return;

    }

    setUrlLink(value);

    setShowAttachUrlModal(false);

  };


  const clearUrl = () => {

    setUrlLink("");

    setTempUrlInput("");

  };


  // File selection
  const handleFileChange = (e, type) => {

    const selectedFile =
      e.target.files?.[0];

    if (!selectedFile) return;

    setFile(selectedFile);

    setFileType(type);

    setPreview(
      URL.createObjectURL(selectedFile)
    );

  };


  const removePreview = () => {

    setFile(null);

    setPreview(null);

    setFileType("");

  };


  // POST
  const handlePost = async () => {

    if (
      !text.trim() &&
      !file &&
      !urlLink
    ) {

      alert(
        "Write something or add media or URL."
      );

      return;

    }

    if (!selectedArea) {

      setShowLocationPicker(true);

      return;

    }

    if (!user?.uid) {

      alert("Please sign in to post.");

      return;

    }

    setPosting(true);

    setUploadProgress(0);

    try {

      const settings =
        await getUserSettings(user.uid);

      const userName =
        settings?.name ||
        user?.displayName ||
        "User";

      const photoURL =
        settings?.photoURL ||
        userPhoto ||
        user?.photoURL ||
        "";


      if (fileType === "video") {

        const uploaded =
          await uploadToCloudinary(file);

        await addVideoPost(
          user.uid,
          text,
          uploaded.secure_url,
          selectedArea,
          userName,
          photoURL,
          uploaded.public_id,
          urlLink
        );

      }

      else if (fileType === "image") {

        const imageUrl =
          await uploadToImgBB(file);

        if (!imageUrl) {

          throw new Error(
            "Image upload failed."
          );

        }

        await addPost(
          user.uid,
          text,
          imageUrl,
          selectedArea,
          userName,
          photoURL,
          urlLink
        );

      }

      else {

        await addPost(
          user.uid,
          text,
          "",
          selectedArea,
          userName,
          photoURL,
          urlLink
        );

      }


      // Reset
      setText("");

      setFile(null);

      setPreview(null);

      setFileType("");

      setUrlLink("");

      setTempUrlInput("");

      setSelectedArea("");

    }

    catch (error) {

      console.error(
        "Post composer error:",
        error
      );

      alert(
        error?.message ||
        "Failed to post."
      );

    }

    finally {

      setPosting(false);

      setUploadProgress(0);

    }

  };


  if (!user) {

    return (
      <div
        style={{
          textAlign: "center",
          margin: "10px 0",
        }}
      >
        <button
          className="composer-post"
          onClick={() => {
            window.location.href = "/login";
          }}
        >
          Sign In to Post
        </button>
      </div>
    );

  }


  return (
    <>
      {/* =========================
          POST COMPOSER
      ========================== */}

      <div
        className="composer"
        style={{
          flexDirection: "column",
          alignItems: "stretch",
        }}
      >

        {/* ROW 1 */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            width: "100%",
            gap: 10,
          }}
        >

          {userPhoto ? (

            <img
              src={userPhoto}
              className="composer-avatar"
              alt="Profile"
              onError={(e) => {
                e.currentTarget.style.display =
                  "none";
              }}
            />

          ) : (

            <FaUserCircle
              className="composer-avatar-icon"
            />

          )}


          <input
            className="composer-input"
            placeholder="Share your update..."
            value={text}
            onChange={(e) =>
              setText(e.target.value)
            }
            style={{
              flex: 1,
            }}
          />

        </div>


        {/* ROW 2 */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            width: "100%",
            marginTop: 10,
          }}
        >

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
            }}
          >

            {/* IMAGE */}
            <label
              className="composer-attach"
              title="Add image"
            >

              <input
                hidden
                type="file"
                accept="image/*"
                onChange={(e) =>
                  handleFileChange(
                    e,
                    "image"
                  )
                }
              />

              <AiOutlineCamera size={20} />

            </label>


            {/* VIDEO */}
            <label
              className="composer-attach"
              title="Add video"
            >

              <input
                hidden
                type="file"
                accept="video/*"
                onChange={(e) =>
                  handleFileChange(
                    e,
                    "video"
                  )
                }
              />

              <AiOutlineVideoCamera
                size={20}
              />

            </label>


            {/* URL */}
            <div
              className="composer-attach"
              onClick={
                openAttachUrlModal
              }
              style={{
                cursor: "pointer",
              }}
            >

              <AiOutlineLink size={20} />

            </div>


            {/* LOCATION */}
            <div
              className="composer-location"
              onClick={() =>
                setShowLocationPicker(true)
              }
            >

              <AiOutlineEnvironment
                size={20}
              />

            </div>

          </div>


          <button
            className="composer-post"
            onClick={handlePost}
            disabled={posting}
          >

            {posting
              ? "Posting..."
              : "Post"}

          </button>

        </div>

      </div>


      {/* URL PILL */}
      {urlLink && (

        <div
          style={{
            padding: "10px 14px",
            display: "flex",
            alignItems: "center",
            gap: 10,
          }}
        >

          <div
            style={{
              background: "#eef6ff",
              padding: "6px 8px",
              borderRadius: 8,
              fontWeight: 600,
            }}
          >

            🔗{" "}
            {(() => {

              try {

                return new URL(
                  urlLink
                ).hostname.replace(
                  /^www\./,
                  ""
                );

              } catch {

                return "Link";

              }

            })()}

          </div>


          <div
            style={{
              color: "#374151",
              fontSize: 13,
              flex: 1,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {urlLink}
          </div>


          <button
            onClick={clearUrl}
            className="btn"
            style={{
              padding: "6px 8px",
              borderRadius: 8,
            }}
          >
            Clear
          </button>

        </div>

      )}


      {/* VIDEO UPLOAD PROGRESS */}
      {fileType === "video" &&
        posting &&
        uploadProgress > 0 && (

          <div
            style={{
              textAlign: "center",
              marginTop: 8,
              marginBottom: 5,
            }}
          >

            <span
              style={{
                fontSize: 14,
                fontWeight: 500,
              }}
            >
              🎥 Uploading...
              {" "}
              {uploadProgress}%
            </span>


            <div
              style={{
                width: "92%",
                height: 6,
                background: "#e5e7eb",
                borderRadius: 4,
                margin:
                  "8px auto 15px auto",
                overflow: "hidden",
              }}
            >

              <div
                style={{
                  width:
                    `${uploadProgress}%`,
                  height: "100%",
                  background: "#007bff",
                  transition:
                    "width 0.3s ease",
                }}
              />

            </div>

          </div>

        )}


      {/* PREVIEW */}
      {preview && (

        <div
          className="image-preview-box"
        >

          {fileType === "video" ? (

            <video
              src={preview}
              controls
              className="image-preview"
            />

          ) : (

            <img
              src={preview}
              alt="Preview"
              className="image-preview"
            />

          )}


          <button
            className="remove-preview-btn"
            onClick={removePreview}
          >

            <AiOutlineCloseCircle
              size={22}
            />

          </button>

        </div>

      )}


      {/* =========================
          LOCATION PICKER
      ========================== */}

      {showLocationPicker && (

        <div
          style={{
            position: "fixed",
            inset: 0,
            background:
              "rgba(0,0,0,0.4)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
          onClick={() =>
            setShowLocationPicker(false)
          }
        >

          <div
            style={{
              background: "#fff",
              borderRadius: 16,
              padding: 20,
              width: "80%",
              maxWidth: 320,
            }}
            onClick={(e) =>
              e.stopPropagation()
            }
          >

            <h4
              style={{
                textAlign: "center",
                marginBottom: 10,
              }}
            >
              Choose your post location
            </h4>


            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 10,
              }}
            >

              {AREAS.map((area) => (

                <label
                  key={area}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    padding:
                      "10px 12px",
                    borderRadius: 12,
                    border:
                      selectedArea === area
                        ? "2px solid #2563eb"
                        : "1px solid #e5e7eb",
                    background:
                      selectedArea === area
                        ? "#eff6ff"
                        : "#fff",
                    cursor: "pointer",
                    fontWeight: 500,
                  }}
                >

                  <input
                    type="radio"
                    value={area}
                    checked={
                      selectedArea === area
                    }
                    onChange={(e) =>
                      setSelectedArea(
                        e.target.value
                      )
                    }
                    style={{
                      marginRight: 10,
                    }}
                  />

                  {area === "All"
                    ? "🌍"
                    : "📍"}{" "}
                  {area}

                </label>

              ))}

            </div>


            <button
              onClick={() => {

                if (!selectedArea) {

                  alert(
                    "Select a location"
                  );

                  return;

                }

                setShowLocationPicker(
                  false
                );

                handlePost();

              }}
              style={{
                width: "100%",
                marginTop: 14,
                padding: 12,
                borderRadius: 12,
                border: "none",
                background:
                  "linear-gradient(135deg,#2563eb,#1d4ed8)",
                color: "#fff",
                fontWeight: 600,
                fontSize: 15,
                cursor: "pointer",
                boxShadow:
                  "0 4px 10px rgba(37,99,235,0.25)",
              }}
            >
              Confirm Location
            </button>

          </div>

        </div>

      )}


      {/* =========================
          URL ATTACH MODAL
      ========================== */}

      {showAttachUrlModal && (

        <div
          className="url-modal-overlay"
          onClick={() =>
            setShowAttachUrlModal(false)
          }
        >

          <div
            className="url-modal-box"
            onClick={(e) =>
              e.stopPropagation()
            }
          >

            <div
              className="url-modal-header"
            >

              <h3>
                Attach URL
              </h3>

              <button
                className="url-modal-close-btn"
                onClick={() =>
                  setShowAttachUrlModal(
                    false
                  )
                }
              >
                Close
              </button>

            </div>


            <input
              value={tempUrlInput}
              onChange={(e) =>
                setTempUrlInput(
                  e.target.value
                )
              }
              placeholder="Paste URL (https://...)"
              className="url-modal-input"
            />


            <div
              className="url-modal-actions"
            >

              <button
                className="url-btn url-btn-primary"
                onClick={attachUrl}
              >
                Attach
              </button>

              <button
                className="url-btn url-btn-light"
                onClick={() =>
                  setTempUrlInput(
                    urlLink
                  )
                }
              >
                Reset
              </button>

              <button
                className="url-btn url-btn-light"
                onClick={clearUrl}
              >
                Clear
              </button>

            </div>


            <p
              className="url-modal-note"
            >
              Link opens inside app in a modal.
            </p>

          </div>

        </div>

      )}

    </>
  );

}